import { validateCustomer, normalizeCustomerPayload, customerApiErrors } from "../validation";
import { ValidationErrorsContext } from "../../../shared/components/FormType/ValidationErrorsContext";
import React, { useEffect, useState, useCallback } from "react";
import BackButton from "../../../shared/components/BackButton";
import Title from "./Title";
import FormContainer from "./Content";
import { api } from "../../../shared/hooks/api";
import { error, success } from "../../../utils/toast";
import { useNavigate } from "react-router-dom";
import { PageContentWrapper } from "../../../shared/styled/PageContentStyle";
import { PRODUCT_CATEGORY } from "../../../data/enums";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";
import CancelModal from "../../../shared/components/CancelModal";

const initialContactPersonData = {
    name: "",
    phone: "",
    tier: "tier 1",
    category: "",
    email: "",
    address: "",
    contact_list: [],
    customer_code: null,
    is_internal: false,
    payable_days: 0,
    credit_limit: 0,
    sales_staff: null,
    tax_id: '',
};

const initialContactListData = [
    {
        id: 1,
        contact: {
            is_default: true,
            name: "",
            phone: "",
            email: "",
            position: "",
        },
    },
];

const CreateCustomer = () => {
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const clearError = (key: string) => setFieldErrors(current => {
        if (!(key in current)) return current;
        const next = { ...current };
        delete next[key];
        return next;
    });
    const [contactPerson, setContactPerson] = useState(
        initialContactPersonData
    );
    const [contactList, setContactList] = useState(initialContactListData);
    const [{ isCreating }, createProvider] = api.post("/customers/");
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");

    let nextId = Math.max(...contactList.map((contact) => contact.id));

    // Mark as modified when customer changes
    useEffect(() => {
        if (contactPerson.name || 
            contactPerson.phone || 
            contactPerson.address || 
            contactPerson.category || 
            contactPerson.customer_code !== null || 
            contactPerson.tax_id !== '' ||
            contactPerson.payable_days !== 0 ||
            contactPerson.credit_limit !== 0 ||
            contactPerson.is_internal ||
            contactList.some((contact) => 
                contact.contact.name || 
                contact.contact.phone || 
                contact.contact.email || 
                contact.contact.position
            )
        ) {
            setIsModified(true);
        }
    }, [contactPerson, contactList]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setNavigationPath(path);
            setShowUnsavedDialog(true);
            return false;
        }

        navigate(path);
        return true;
    }, [isModified, navigate]);

    const handleBackClick = () => {
        confirmNavigation("/customers/");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        await handleSubmit();
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const handleChangePersonContact = (updatePersonContact: any) => {
        setIsModified(true);
        setContactPerson({
            ...contactPerson,
            name: updatePersonContact.name,
            phone: updatePersonContact.phone,
            category: typeof updatePersonContact.category === 'string' 
                ? updatePersonContact.category
                : updatePersonContact.category?.join(';') || '',
            email: updatePersonContact.email,
            customer_code: updatePersonContact.customer_code,
            address: updatePersonContact.address,
            is_internal: updatePersonContact.is_internal,
            payable_days: updatePersonContact.payable_days,
            credit_limit: updatePersonContact.credit_limit,
            sales_staff: updatePersonContact.sales_staff,
            tax_id: updatePersonContact.tax_id
        });
    };

    const handleChangeContactList = (updateContact: any) => {
        setIsModified(true);
        setContactList(
            contactList.map((contact) => {
                if (contact.id === updateContact.id) {
                    return {
                        ...contact,
                        contact: {
                            ...contact.contact,
                            is_default: updateContact.is_default,
                            name: updateContact.name,
                            phone: updateContact.phone,
                            email: updateContact.email,
                            position: updateContact.position,
                        },
                    };
                } else if (updateContact.is_default) {
                    return {
                        ...contact,
                        contact: {
                            ...contact.contact,
                            is_default: false,
                        },
                    };
                }
                return contact;
            })
        );
    };

    const handleAddContact = () => {
        setContactList([
            ...contactList,
            {
                id: ++nextId,
                contact: {
                    is_default: false,
                    name: "",
                    phone: "",
                    email: "",
                    position: "",
                },
            },
        ]);
    };

    const handleSubmit = () => {
        setValidated(true);
        const errors = validateCustomer({ ...contactPerson, contact_list: contactList });
        setFieldErrors(errors);
        if (Object.keys(errors).length) return;

        const responseData = {
            ...contactPerson,
            sales_staff: contactPerson.sales_staff && typeof contactPerson.sales_staff === 'object' 
                ? (contactPerson.sales_staff as any).id || null
                : contactPerson.sales_staff,
            contact_list: contactList.map(({ id, contact }) => {
                return {
                    id,
                    contact: {
                        ...contact,
                    },
                };
            }),
        };
        return createProvider(normalizeCustomerPayload(responseData))
            .then((data: any) => {
                success("Thành Công", "Tạo mới khách hàng thành công");
                setIsModified(false);
                navigate(`/customers/${data.id}`);
                return true;
            })
            .catch((err: any) => {
                setFieldErrors(customerApiErrors(err));
                console.error('Error creating product:', err);
                error("Lỗi tạo mới khách hàng", err);
                return false;
            });
    };

    const handleDeleteContact = (contactId: number) => {
        setContactList(
            contactList.filter((contact) => contact.id !== contactId)
        );
    };

    return (
        <ValidationErrorsContext.Provider value={{ errors: fieldErrors, clearError }}>
        <PageContentWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            <Title setValidated={setValidated} submitForm={handleSubmit} isLoading={isCreating} />
            <FormContainer
                contactList={contactList}
                onChangePersonContact={handleChangePersonContact}
                onChangeContactList={handleChangeContactList}
                onDeleteContactList={handleDeleteContact}
                onAddContactList={handleAddContact}
                validated={validated}
            />

            <CancelModal
                open={showUnsavedDialog}
                handleConfirm={handleSaveAndNavigate}
                handleClose={handleDontSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
                isLoading={isCreating}
            />
        </PageContentWrapper>
        </ValidationErrorsContext.Provider>
    );
};

export default CreateCustomer;
